import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { useTurnstile, TurnstileMount } from "@/components/TurnstileWidget";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const turnstile = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        password: pw,
        turnstile_token: turnstile.token,
        _hp_website: honeypot,
      });
      toast.success("Password reset — you're signed in.");
      await refresh();
      const role = data?.user?.role;
      navigate(role === "business" ? "/dashboard/business" : role === "admin" ? "/admin" : "/dashboard/customer");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      turnstile.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#B31E1E] mb-6">
        <ArrowLeft size={14} /> Back to log in
      </Link>
      <Card className="rounded-[24px] p-8 border border-border" data-testid="reset-password-card">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={18} className="text-[#B31E1E]" />
          <div className="overline text-gray-500">Choose a new password</div>
        </div>
        <h1 className="font-display text-3xl tracking-tight">Set a new password</h1>
        <p className="mt-2 text-gray-600 text-sm leading-relaxed">
          Pick something memorable but tough to guess. At least 6 characters.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="rp-pw">New password</Label>
            <Input
              id="rp-pw"
              type="password"
              required
              minLength={6}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="rounded-md mt-1"
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <Label htmlFor="rp-pw2">Confirm new password</Label>
            <Input
              id="rp-pw2"
              type="password"
              required
              minLength={6}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="rounded-md mt-1"
              data-testid="reset-password-confirm"
            />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="reset-password-error">{error}</p>}
          <input
            type="text"
            name="_hp_website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
            data-testid="reset-password-honeypot"
          />
          <TurnstileMount widgetRef={turnstile.widgetRef} sitekey={turnstile.sitekey} />
          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] h-11"
            data-testid="reset-password-submit"
          >
            {submitting ? "Resetting…" : "Reset password & sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
